import * as React from "react";

import { conversationsApi } from "@/api/conversations";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatStream } from "@/components/chat/ChatStream";
import { Composer } from "@/components/chat/Composer";
import { ChatLayout } from "@/components/layout/ChatLayout";
import { useChatWebSocket } from "@/hooks/useChatWebSocket";
import { uuidv4 } from "@/lib/utils";
import { useChatStore } from "@/stores/chat";
import type { Uuid } from "@/types";

export function ChatPage(): JSX.Element {
  const activeId = useChatStore((s) => s.activeConversationId);
  const setConversations = useChatStore((s) => s.setConversations);
  const setActive = useChatStore((s) => s.setActiveConversation);
  const setMessages = useChatStore((s) => s.setMessages);
  const updateWidgetStatus = useChatStore((s) => s.updateWidgetStatus);

  const { send, isReady } = useChatWebSocket(activeId);

  React.useEffect(() => {
    void (async () => {
      const list = await conversationsApi.list();
      setConversations(list.items);
      if (list.items.length === 0) {
        const conv = await conversationsApi.create("Hội thoại đầu tiên");
        setConversations([conv]);
        setActive(conv.id);
      } else if (list.items[0]) {
        setActive(list.items[0].id);
      }
    })();
  }, [setConversations, setActive]);

  React.useEffect(() => {
    if (!activeId) return;
    void (async () => {
      const list = await conversationsApi.messages(activeId);
      setMessages(list.items);
    })();
  }, [activeId, setMessages]);

  const handleSubmit = (text: string): void => {
    if (!isReady || !activeId) return;
    const { selectedProvider, selectedModel } = useChatStore.getState();
    const tempUserMsg = {
      id: uuidv4(),
      conversation_id: activeId,
      role: "user" as const,
      content: { type: "text" as const, text },
      metadata: { _optimistic: true },
      created_at: new Date().toISOString(),
    };
    useChatStore.getState().appendMessage(tempUserMsg);
    send({
      type: "user_message",
      text,
      llm_provider: selectedProvider,
      llm_model: selectedModel,
    });
  };

  const handleWidgetSubmit = (widget_id: Uuid, data: Record<string, unknown>): void => {
    updateWidgetStatus(widget_id, "submitted");
    send({ type: "widget_submit", widget_id, data });
  };

  const handleWidgetApproval = (
    widget_id: Uuid,
    action: "save" | "cancel" | "approve" | "reject" | "request_change",
    reason?: string,
  ): void => {
    if (action === "save" || action === "approve") {
      updateWidgetStatus(widget_id, "approved");
    } else if (action === "reject" || action === "cancel") {
      updateWidgetStatus(widget_id, "rejected");
    }
    send({ type: "approval", widget_id, action, reason });
  };

  return (
    <ChatLayout>
      <ChatHeader />
      <ChatStream
        onWidgetSubmit={handleWidgetSubmit}
        onWidgetApproval={handleWidgetApproval}
      />
      <Composer
        onSubmit={handleSubmit}
        disabled={!isReady}
        placeholder={
          !activeId
            ? "Đang tải hội thoại..."
            : !isReady
              ? "Đang kết nối..."
              : "Hỏi gì AI..."
        }
      />
    </ChatLayout>
  );
}
